import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faFileExcel,
} from '@fortawesome/free-solid-svg-icons';
import SearchInput from '../components/common/SearchInput';
import Pagination from '../components/common/Pagination';
import ByClientLatestMonthTable from '../components/ByClientLatestMonthTable';
import ByEachClientLatestMonthTable from '../components/ByEachClientLatestMonthTable';
import { useSelector } from 'react-redux';
import { authSelector } from '../features/auth/slice';
import SectionTabs from '../components/SectionTabs';
import { Users, Disc, Music, Globe, User, Library, ListMusic, Package, Boxes } from 'lucide-react';
import usePhysicalExists from '../hooks/usePhysicalExists';

function ByClientLatestMonthPage() {
  const { me } = useSelector(authSelector.selectAuthState);
  const [loading, setLoading] = useState(false);
  const [byClientLatestMonth, setByClientLatestMonth] = useState([]);
  const [month, setMonth] = useState('');
  const [monthList, setMonthList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [clientType, setClientType] = useState('');
  const pageSize = 15;

  const adminYn = me?.adminYn === 'Y' ? 'Y' : 'N';
  const isAdmin = me?.adminYn === 'Y';
  const isSuperAdmin = me?.superAdminYn === 'Y';
  const [isLocked, setIsLocked] = useState(false);
  const [lockLoading, setLockLoading] = useState(false);

  const fetchData = useCallback(
    async (targetMonth, search = '') => {
      setLoading(true);
      try {
        const params = {
          search,
          page: currentPage,
          limit: pageSize,
          adminYn,
          ...(clientType && { clientType }),
        };
        if (targetMonth) {
          params.calculateMonth = targetMonth;
        }

        const response = await axios.get(`/ByClient/byClientLatestMonth`, {
          params,
        });
        setByClientLatestMonth(response.data.items);
        setTotalPages(response.data.totalPages);
        setTotalCount(response.data.totalCount);
      } catch (error) {
        console.error('서버 접속 에러:', error);
      } finally {
        setLoading(false);
      }
    },
    [currentPage, pageSize, adminYn, clientType]
  );

  useEffect(() => {
    const fetchMonthList = async () => {
      const res = await axios.get(`/ByClient/getMonthList`);
      const months = res.data || [];
      setMonthList(months);
      if (months.length > 0) {
        setMonth((prev) => prev || months[0]);
      }
    };
    fetchMonthList();
  }, []);

  useEffect(() => {
    if (month) {
      setCurrentPage(1);
    }
  }, [month]);

  useEffect(() => {
    setCurrentPage(1);
  }, [clientType]);

  useEffect(() => {
    if (me && month) {
      fetchData(month, searchKeyword);
    }
  }, [me, month, currentPage, searchKeyword, fetchData]);

  useEffect(() => {
    if (!isAdmin || !month) return;
    axios.get('/ByClient/monthLock', { params: { calculateMonth: month } })
      .then((res) => setIsLocked(res.data.isLocked))
      .catch(() => {});
  }, [isAdmin, month]);

  const handleToggleLock = async () => {
    setLockLoading(true);
    try {
      const res = await axios.post('/ByClient/monthLock', { calculateMonth: month });
      setIsLocked(res.data.isLocked);
    } catch {
      alert('오류가 발생했습니다.');
    } finally {
      setLockLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const currentIndex = monthList.indexOf(month);

  const handlePrevMonth = () => {
    if (currentIndex < monthList.length - 1) {
      const newMonth = monthList[currentIndex + 1];
      setMonth(newMonth);
      setCurrentPage(1);
    }
  };

  const handleNextMonth = () => {
    if (currentIndex > 0) {
      const newMonth = monthList[currentIndex - 1];
      setMonth(newMonth);
      setCurrentPage(1);
    }
  };

  const formatMonth = (m) => {
    if (!m) return '';
    const [y, mo] = m.split('-');
    const monthStr = `${y}년 ${parseInt(mo, 10)}월`;
    return me?.adminYn === 'Y'
      ? `${monthStr} 권리사별 정산`
      : `${monthStr} 정산`;
  };

  const filteredClients = byClientLatestMonth;

  const handleDownload = async () => {
    try {
      const params = { search: searchTerm, page: 1, limit: 10000 };
      if (month) params.calculateMonth = month;

      const response = await axios.get(`/ByClient/byClientLatestMonth`, {
        params,
      });
      const allClients = response.data.items;
      const exportClients = allClients.filter(
        (c) =>
          c.calculateMonth === month &&
          c.client.name.includes(searchTerm) &&
          c.totalAmount > 0
      );

      const toBusinessRow = (c) => {
        const vt = Math.round(c.totalAmount * 0.1);
        return {
          권리사: c.client.name,
          인세총액: c.receiveAmount,
          별도정산: c.commissionShareAmount || 0,
          전월이월: c.prevAmount,
          선급: -(c.advanceAmount || 0),
          지급총액: c.totalAmount,
          부가세: vt,
          실지급액: c.totalAmount + vt,
          차월이월: c.nextAmount,
          은행명: c.client.bank,
          계좌번호: c.client.account,
          예금주: c.client.holder,
          전화번호: `${c.client.phone1}${c.client.phone2}${c.client.phone3}`,
          이메일: `${c.client?.emailId ?? ''}@${c.client?.emailDomain ?? ''}`,
        };
      };

      const toPersonalRow = (c) => {
        const wt = Math.round(c.totalAmount * 0.033);
        return {
          권리사: c.client.name,
          인세총액: c.receiveAmount,
          별도정산: c.commissionShareAmount || 0,
          전월이월: c.prevAmount,
          선급: -(c.advanceAmount || 0),
          지급총액: c.totalAmount,
          원천세: wt,
          실지급액: c.totalAmount - wt,
          차월이월: c.nextAmount,
          주민등록번호: `${c.client.rrn1 ?? ''}-${c.client.rrn2 ?? ''}`,
          주소: `${c.client.address ?? ''} ${c.client.detailAddress ?? ''}`.trim(),
          은행명: c.client.bank,
          계좌번호: c.client.account,
          예금주: c.client.holder,
          전화번호: `${c.client.phone1}${c.client.phone2}${c.client.phone3}`,
          이메일: `${c.client?.emailId ?? ''}@${c.client?.emailDomain ?? ''}`,
        };
      };

      const businessClients = exportClients.filter((c) => c.client.type === '사업자');
      const personalClients = exportClients.filter((c) => c.client.type === '개인');

      // 선급 지급건 (당월 advanceAmount > 0) — 검색어/월 필터만 적용, totalAmount 무관
      const advanceClients = allClients.filter(
        (c) =>
          c.calculateMonth === month &&
          c.client.name.includes(searchTerm) &&
          Number(c.advanceAmount) > 0
      );

      // 선급 지급건도 세금 처리를 위해 본 테이블과 동일한 컬럼 구조 유지
      // 지급총액 = advanceAmount, 부가세/원천세/실지급액은 그 금액 기준으로 재계산
      const toAdvanceBusinessRow = (c) => {
        const advAmount = Number(c.advanceAmount) || 0;
        const vt = Math.round(advAmount * 0.1);
        return {
          권리사: c.client.name,
          인세총액: 0,
          별도정산: 0,
          전월이월: 0,
          선급: advAmount,
          지급총액: advAmount,
          부가세: vt,
          실지급액: advAmount + vt,
          차월이월: 0,
          은행명: c.client.bank,
          계좌번호: c.client.account,
          예금주: c.client.holder,
          전화번호: `${c.client.phone1}${c.client.phone2}${c.client.phone3}`,
          이메일: `${c.client?.emailId ?? ''}@${c.client?.emailDomain ?? ''}`,
        };
      };

      const toAdvancePersonalRow = (c) => {
        const advAmount = Number(c.advanceAmount) || 0;
        const wt = Math.round(advAmount * 0.033);
        return {
          권리사: c.client.name,
          인세총액: 0,
          별도정산: 0,
          전월이월: 0,
          선급: advAmount,
          지급총액: advAmount,
          원천세: wt,
          실지급액: advAmount - wt,
          차월이월: 0,
          주민등록번호: `${c.client.rrn1 ?? ''}-${c.client.rrn2 ?? ''}`,
          주소: `${c.client.address ?? ''} ${c.client.detailAddress ?? ''}`.trim(),
          은행명: c.client.bank,
          계좌번호: c.client.account,
          예금주: c.client.holder,
          전화번호: `${c.client.phone1}${c.client.phone2}${c.client.phone3}`,
          이메일: `${c.client?.emailId ?? ''}@${c.client?.emailDomain ?? ''}`,
        };
      };

      const appendAdvanceSection = (ws, rows, toRow) => {
        if (!rows.length) return;
        // 구분선 + 제목
        XLSX.utils.sheet_add_aoa(ws, [[], ['=== 선급 지급 ===']], { origin: -1 });
        // 본 테이블과 동일한 컬럼 구조로 헤더+데이터 이어붙임
        XLSX.utils.sheet_add_json(ws, rows.map(toRow), { origin: -1, skipHeader: false });
      };

      const wb = XLSX.utils.book_new();
      const wsBusiness = XLSX.utils.json_to_sheet(businessClients.map(toBusinessRow));
      const wsPersonal = XLSX.utils.json_to_sheet(personalClients.map(toPersonalRow));

      appendAdvanceSection(
        wsBusiness,
        advanceClients.filter((c) => c.client.type === '사업자'),
        toAdvanceBusinessRow
      );
      appendAdvanceSection(
        wsPersonal,
        advanceClients.filter((c) => c.client.type === '개인'),
        toAdvancePersonalRow
      );

      XLSX.utils.book_append_sheet(wb, wsBusiness, '사업자');
      XLSX.utils.book_append_sheet(wb, wsPersonal, '개인');
      XLSX.writeFile(wb, `${formatMonth(month)}.xlsx`);
    } catch (error) {
      console.error('엑셀 다운로드 중 에러:', error);
    }
  };

  const handleAnnualDownload = async () => {
    try {
      const response = await axios.get(
        '/ByClient/getAllClientsForAnnualDownload',
        {
          params: { adminYn, search: searchTerm },
        }
      );

      const { items, months } = response.data;
      const sortedMonths = months.sort(); // 오름차순 정렬 (최신월은 마지막)

      // 최신월 기준 정렬
      const lastMonth = sortedMonths[sortedMonths.length - 1];
      const sortedItems = items.sort(
        (a, b) => (b[lastMonth] || 0) - (a[lastMonth] || 0)
      );

      const exportData = sortedItems.map((item) => {
        const row = { 권리사: item.clientName };
        sortedMonths.forEach((month) => {
          row[month] = item[month] ?? 0;
        });
        return row;
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '최근 1년 권리사별 정산');
      XLSX.writeFile(wb, `최근1년_권리사별_월별정산.xlsx`);
    } catch (err) {
      console.error('연간 다운로드 오류:', err);
      alert('엑셀 다운로드 중 오류가 발생했습니다.');
    }
  };

  const refetchLatestData = () => {
    fetchData(month, searchKeyword);
  };

  const hasPhysical = usePhysicalExists();
  const reportTabs = [
    { label: isAdmin ? '당월권리사별' : '월별리포트', to: '/ByClientLatestMonth', icon: Users },
    { label: '당월앨범별', to: '/ByMonthAlbums', icon: Disc },
    { label: '당월곡별', to: '/ByMonthSongs', icon: Music },
    { label: '당월플랫폼별', to: '/ByMonthPlatforms', icon: Globe },
    { label: '당월아티스트별', to: '/ByMonthArtists', icon: User },
    ...(hasPhysical ? [{ label: '당월음반별', to: '/ByPhysicalMonth', icon: Package }] : []),
    { label: '앨범별누적', to: '/ByAlbums', icon: Library },
    { label: '곡별누적', to: '/BySongs', icon: ListMusic },
    ...(hasPhysical ? [{ label: '음반누적', to: '/ByPhysicalTotal', icon: Boxes }] : []),
  ];

  return (
    <>
        <div className="flex mb-8 justify-center items-center gap-5">
          {isAdmin && (
            <button
              onClick={handlePrevMonth}
              disabled={currentIndex === monthList.length - 1}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
          )}

          <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
            {isAdmin ? formatMonth(month) : '월별리포트'}
          </h1>

          {isAdmin && (
            <button
              onClick={handleNextMonth}
              disabled={currentIndex === 0 || !filteredClients.length}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          )}
        </div>
        <SectionTabs items={reportTabs} />
        {isAdmin && (
          <div className="flex gap-2 mb-3">
            {[
              { label: '전체', value: '' },
              { label: '사업자', value: '사업자' },
              { label: '개인', value: '개인' },
            ].map(({ label, value }) => (
              <button
                key={value}
                onClick={() => setClientType(value)}
                className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                  clientType === value
                    ? 'bg-sky-500 text-white'
                    : 'bg-white/10 text-slate-400 hover:bg-white/20 hover:text-white'
                }`}>
                {label}
              </button>
            ))}
          </div>
        )}
        <SearchInput
          placeholder={isAdmin ? '권리사명 검색' : '정산월 검색'}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
            setSearchKeyword(e.target.value);
          }}
          onSearch={() => {
            setCurrentPage(1);
            setSearchKeyword(searchTerm);
          }}
          onClear={() => {
            setSearchTerm('');
            setSearchKeyword('');
            setCurrentPage(1);
          }}
        />

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mr-3"></div>
            <div className="text-sm text-slate-500">데이터 불러오는 중...</div>
          </div>
        ) : (
          <>
            {isAdmin ? (
              <ByClientLatestMonthTable
                data={filteredClients}
                isLatestMonth={isSuperAdmin ? true : (month === monthList[0] && !isLocked)}
                isSuperAdmin={isSuperAdmin}
                onUpdate={refetchLatestData}
                totalCount={totalCount}
                currentPage={currentPage}
                pageSize={pageSize}
              />
            ) : (
              <ByEachClientLatestMonthTable
                data={filteredClients}
                isLatestMonth={true}
                onUpdate={fetchData}
                totalCount={totalCount}
                currentPage={currentPage}
                pageSize={pageSize}
                me={me}
              />
            )}

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />

            {isAdmin && (
              <div className="w-full flex justify-between items-center mt-6">
                {isSuperAdmin ? (
                  <button
                    onClick={handleToggleLock}
                    disabled={lockLoading}
                    className={`flex items-center px-4 py-2 rounded-xl text-white text-sm font-semibold transition-colors ${
                      isLocked
                        ? 'bg-amber-500 hover:bg-amber-600'
                        : 'bg-slate-500 hover:bg-slate-600'
                    }`}>
                    {isLocked ? '지급취소' : '지급완료'}
                  </button>
                ) : (
                  <div />
                )}
                <div className="flex space-x-3">
                  <button
                    onClick={handleAnnualDownload}
                    className="flex items-center px-4 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 text-sm font-semibold transition-colors">
                    <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
                    다운로드
                  </button>
                  <button
                    onClick={handleDownload}
                    className="flex items-center px-4 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 text-sm font-semibold transition-colors">
                    <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
                    월정산
                  </button>
                </div>
              </div>
            )}
          </>
        )}
    </>
  );
}

export default ByClientLatestMonthPage;
